import { useState } from "react";
import { Helmet } from "react-helmet";
import { useHistory, useLocation } from "react-router";
import { FormProvider, useForm } from "react-hook-form";
import classnames from "classnames";
import qs from "query-string";
import validator from "validator";
import { ApolloError, gql, useMutation } from "@apollo/client";
import Layout from "../../components/Layout";
import LayoutCard from "../../components/LayoutCard";
import TextInput from "../../components/TextInput";
import Button from "../../components/Button";
import { Heading, Text } from "../../components/Type";
import { CheckMarkRounded, Dot, Info, ArrowRight } from "../../components/Icons/general-interface-1";
import styles from "./index.module.scss";

const SET_PASSWORD = gql`
  mutation ResetPassword(
    $password: String!
    $passwordConfirmation: String!
    $code: String!
  ) {
    resetPassword(
      password: $password
      passwordConfirmation: $passwordConfirmation
      code: $code
    ) {
      user {
        email
      }
    }
  }
`;

const SetPassword = () => {
  const location = useLocation();
  const history = useHistory();
  const methods = useForm();
  const [error, setError] = useState<string | undefined>();
  const [passwordScore, setPasswordScore] = useState<string>("000");
  const { code, reset } = qs.parse(location.search);
  const needShowResetPassword = reset === 'true';
  const [resetPassword, { data }] = useMutation(SET_PASSWORD, {
    onError: (error: ApolloError) => {
      const msg =
        error.graphQLErrors?.[0]?.extensions?.exception?.data?.data[0]
          ?.messages[0]?.message || "An unknown error has occurred";
      setError(msg);
    },
  });
  const { errors } = methods.formState;
  const MIN_PASSWORD_LENGTH = 7;
  const hasLength = passwordScore.charAt(2) === "3";
  const hasUpper = passwordScore.charAt(1) === "3";
  const hasSymbol = passwordScore.charAt(0) === "3";

  const onSubmit = async (values) => {
    setError(undefined);
    try {
      if (!code) {
        throw new Error(
          "Please use the link in the reset password email that was sent."
        );
      }
      await resetPassword({
        variables: {
          password: values.password,
          passwordConfirmation: values.passwordConfirmation,
          code,
        },
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      setError(err.message);
    }
  };

  const getPasswordScore = (value: string) => {
    if (!value) return "000";
    let score = validator.isStrongPassword(value, {
      returnScore: true,
      pointsPerUnique: 0,
      pointsPerRepeat: 0,
      pointsForContainingLower: 0,
      pointsForContainingUpper: 30,
      pointsForContainingNumber: 0,
      pointsForContainingSymbol: 300,
    }) as unknown as number;
    if (value.length >= MIN_PASSWORD_LENGTH) {
      score += 3;
    }
    return score.toString().padStart(3, "0");
  };

  const testPassword = (e: React.ChangeEvent<HTMLInputElement>) => {
    const score = getPasswordScore(e.currentTarget.value);
    setPasswordScore(score);
  };

  return (
    <Layout>
      <Helmet>
        <title>Motion Reset Password</title>
      </Helmet>
      <LayoutCard
        error={error}
        header={
          <Heading size="h4">{data ? "Successful!" : needShowResetPassword ? "Reset your password" : "Set password"}</Heading>
        }
      >
        {data ? (
          <>
            <Text className={styles.successMeaasge}>
              Your password has been successfully changed. Log into your account
              below.
            </Text>
            <div className={styles.successButtonRow}>
              <Button
                primary
                icon={<ArrowRight />}
                onClick={() =>
                  history.push({
                    pathname: "/login",
                    search: qs.stringify({
                      email: data.resetPassword.user.email,
                    }),
                  })
                }
              >
                Login
              </Button>
            </div>
          </>
        ) : (
          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)}>
              <TextInput
                id="password"
                label="Password"
                type="password"
                showEyeIcon
                disableErrors
                fieldOptions={{
                  required: "Password is required",
                  validate: (value) =>
                    getPasswordScore(value) !== "333"
                      ? "A strong password is required"
                      : undefined,
                }}
                onChange={testPassword}
              />
              <div className={styles.validations}>
                <Text
                  size="small"
                  className={classnames(
                    styles.validation,
                    hasLength && styles.valid
                  )}
                >
                  {hasLength ? <CheckMarkRounded /> : <Dot />} 7 characters
                  minimum
                </Text>
                <Text
                  size="small"
                  className={classnames(
                    styles.validation,
                    hasSymbol && styles.valid
                  )}
                >
                  {hasSymbol ? <CheckMarkRounded /> : <Dot />} One special
                  character
                </Text>
                <Text
                  size="small"
                  className={classnames(
                    styles.validation,
                    hasUpper && styles.valid
                  )}
                >
                  {hasUpper ? <CheckMarkRounded /> : <Dot />} One uppercase
                  character
                </Text>
              </div>
              <TextInput
                id="passwordConfirmation"
                label="Confirm Password"
                type="password"
                showEyeIcon
                disableErrors
                fieldOptions={{
                  required: "Password confirmation is required",
                  validate: {
                    match: (value) => {
                      const password = methods.getValues("password");
                      return password !== value
                        ? "Passwords do not match"
                        : undefined;
                    },
                  },
                }}
              />
              {errors.password || errors.passwordConfirmation ? (
                <div className={styles.error}>
                  <Info />
                  {errors.password
                    ? errors.password.message
                    : errors.passwordConfirmation &&
                      errors.passwordConfirmation.message}
                </div>
              ) : null}
              <div className={styles.buttonRow}>
                <Button bold primary submit icon={<ArrowRight />}>
                  {needShowResetPassword ? 'Reset password' : 'Set password'}
                </Button>
              </div>
            </form>
          </FormProvider>
        )}
      </LayoutCard>
    </Layout>
  );
};

export default SetPassword;
